import { InboxOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  Card,
  Col,
  Image as AntImage,
  Input,
  message,
  Radio,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import React from 'react';
import { get, useFormContext, useWatch } from 'react-hook-form';

import {
  useBannerCategories,
  useBannerProducts,
} from '../../hooks/useMarketplaceOptions';
import type { BannerRootData } from '../../schemas/bannerSchema';
import {
  BannerDestinationType,
  BannerImageFieldPath,
  BannerImageUploadDefinition,
  BannerImageValue,
  BannerSlot,
} from '../../types/bannerManagement';
import { validateImageDimensions } from '../../utils/bannerImageValidation';
import { Section } from '../Section';

const { Text } = Typography;

const destinationTooltipText =
  'Defina para onde o usuário será direcionado ao clicar no banner. Você pode escolher entre uma URL externa, um produto ou uma categoria do marketplace.';

const destinationTypeOptions = [
  { label: 'Link', value: BannerDestinationType.Link },
  { label: 'Produto', value: BannerDestinationType.Product },
  { label: 'Categoria', value: BannerDestinationType.Category },
] as const;

const topSlotUploads: BannerImageUploadDefinition[] = [
  {
    label: 'Web',
    width: 1496,
    height: 233,
    fieldPath: 'content.images.web' as BannerImageFieldPath,
  },
  {
    label: 'Tablet',
    width: 981,
    height: 288,
    fieldPath: 'content.images.tablet' as BannerImageFieldPath,
  },
  {
    label: 'Mobile (Web)',
    width: 793,
    height: 233,
    fieldPath: 'content.images.mobile' as BannerImageFieldPath,
  },
];

const middleOrBottomUpload: BannerImageUploadDefinition = {
  label: 'Imagem',
  width: 900,
  height: 360,
  fieldPath: 'content.images.single' as BannerImageFieldPath,
};

const getImageConfigBySlot = (slot?: string): BannerImageUploadDefinition[] => {
  if (slot === BannerSlot.Top) {
    return topSlotUploads;
  }

  return [middleOrBottomUpload];
};

const getFieldErrorMessage = (error: unknown): string | undefined => {
  if (typeof error === 'object' && error && 'message' in error) {
    return String((error as { message?: string }).message);
  }

  return undefined;
};

export const BannerContentStep: React.FC = () => {
  const {
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext<BannerRootData>();

  const slot = useWatch({
    name: 'configuration.slotType',
  });

  const destinationType = useWatch({
    name: 'content.destinationType',
  });

  const { options: categoryOptions, isLoading: isLoadingCategories } =
    useBannerCategories();

  const { options: productOptions, isLoading: isLoadingProducts } =
    useBannerProducts();

  const imageDefinitions = getImageConfigBySlot(slot);

  const applyImage = async (
    file: File,
    definition: BannerImageUploadDefinition
  ): Promise<boolean> => {
    const isValid = await validateImageDimensions(
      file,
      definition.width,
      definition.height
    );

    if (!isValid) {
      setError(definition.fieldPath, {
        type: 'manual',
        message: `A imagem deve ter ${definition.width} x ${definition.height} pixels.`,
      });
      message.error(
        `A imagem deve ter ${definition.width} x ${definition.height} pixels.`
      );
      return false;
    }

    const preview = URL.createObjectURL(file);
    const currentValue = watch(
      definition.fieldPath as never
    ) as BannerImageValue;

    if (currentValue?.preview?.startsWith('blob:')) {
      URL.revokeObjectURL(currentValue.preview);
    }

    setValue(
      definition.fieldPath,
      {
        file,
        preview,
        name: file.name,
      } as never,
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );

    clearErrors(definition.fieldPath as never);

    return true;
  };

  const handleDestinationTypeChange = (value: string) => {
    setValue('content.destinationType', value as never, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setValue(
      'content.destinationLink',
      value === BannerDestinationType.Link ? 'https://' : (null as never),
      { shouldDirty: true }
    );
    setValue('content.destinationProduct', null as never, {
      shouldDirty: true,
    });
    setValue('content.destinationCategory', null as never, {
      shouldDirty: true,
    });
  };

  const destinationTypeError = get(errors, 'content.destinationType');
  const destinationLinkError = get(errors, 'content.destinationLink');
  const destinationProductError = get(errors, 'content.destinationProduct');
  const destinationCategoryError = get(errors, 'content.destinationCategory');
  const nameError = get(errors, 'content.name');

  const destinationTypeErrorMessage =
    getFieldErrorMessage(destinationTypeError);
  const destinationLinkErrorMessage =
    getFieldErrorMessage(destinationLinkError);
  const destinationProductErrorMessage = getFieldErrorMessage(
    destinationProductError
  );
  const destinationCategoryErrorMessage = getFieldErrorMessage(
    destinationCategoryError
  );
  const nameErrorMessage = getFieldErrorMessage(nameError);

  return (
    <Section
      title='Imagem do carrossel'
      subtitle='Configure o conteúdo exibido no banner'
      type='primary'
    >
      <Card
        title='Imagem do carrossel'
        style={{ border: '1px solid #303030', borderRadius: 10 }}
        styles={{
          body: { padding: 20 },
          header: {
            fontWeight: 600,
            color: 'rgba(255, 255, 255, 0.88)',
            background: 'rgba(255, 255, 255, 0.04)',
            borderBottom: '1px solid #303030',
          },
        }}
      >
        <Space direction='vertical' size={20} style={{ width: '100%' }}>
          <Space direction='vertical' size={8} style={{ width: '100%' }}>
            <Text strong>Nome do banner</Text>
            <Input
              value={watch('content.name') || ''}
              onChange={event =>
                setValue('content.name', event.target.value, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              status={nameError ? 'error' : undefined}
              placeholder='Escreva o nome do banner'
              size='large'
              style={{ background: 'transparent' }}
            />
            {nameErrorMessage && (
              <Text type='danger' style={{ fontSize: 12, display: 'block' }}>
                {nameErrorMessage}
              </Text>
            )}
          </Space>

          <Row gutter={[16, 16]}>
            {imageDefinitions.map(definition => {
              const imageValue = watch(
                definition.fieldPath as never
              ) as BannerImageValue | null;
              const fieldError = get(errors, definition.fieldPath);
              const imagePreview = imageValue?.preview;
              const fieldErrorMessage = getFieldErrorMessage(fieldError);

              return (
                <Col
                  key={definition.fieldPath}
                  xs={24}
                  md={slot === BannerSlot.Top ? 8 : 24}
                >
                  <Space
                    direction='vertical'
                    size={8}
                    style={{ width: '100%' }}
                  >
                    <Text strong>{definition.label}</Text>
                    <Upload.Dragger
                      accept='image/png,image/jpeg,image/webp'
                      multiple={false}
                      maxCount={1}
                      showUploadList={false}
                      beforeUpload={async file => {
                        const wasApplied = await applyImage(
                          file as File,
                          definition
                        );
                        return wasApplied ? false : Upload.LIST_IGNORE;
                      }}
                      style={{
                        minHeight: 162,
                        borderColor: fieldError ? '#ff4d4f' : '#3a3a3a',
                        background: 'rgba(255, 255, 255, 0.02)',
                        borderRadius: 8,
                      }}
                    >
                      {imagePreview ? (
                        <AntImage
                          src={imagePreview}
                          alt={definition.label}
                          preview={{ mask: 'Visualizar' }}
                          style={{
                            width: '100%',
                            maxHeight: 250,
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <div style={{ textAlign: 'center', padding: 12 }}>
                          <InboxOutlined
                            style={{
                              color: '#1677ff',
                              fontSize: 30,
                              marginBottom: 8,
                            }}
                          />
                          <p>
                            <strong style={{ color: '#1668DC' }}>
                              Clique para carregar
                            </strong>{' '}
                            ou arraste e solte
                          </p>
                          <Text type='secondary'>
                            {definition.width} x {definition.height} pixels
                          </Text>
                        </div>
                      )}
                    </Upload.Dragger>
                    {fieldErrorMessage && (
                      <Text
                        type='danger'
                        style={{ fontSize: 12, display: 'block' }}
                      >
                        {fieldErrorMessage}
                      </Text>
                    )}
                  </Space>
                </Col>
              );
            })}
          </Row>

          <Space direction='vertical' size={8} style={{ width: '100%' }}>
            <Space size={6}>
              <Text strong>Destino do Item</Text>
              <Tooltip title={destinationTooltipText}>
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>
            <Radio.Group
              value={destinationType}
              onChange={event =>
                handleDestinationTypeChange(event.target.value)
              }
            >
              <Space size={20}>
                {destinationTypeOptions.map(option => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
            {destinationTypeErrorMessage && (
              <Text type='danger' style={{ fontSize: 12, display: 'block' }}>
                {destinationTypeErrorMessage}
              </Text>
            )}
          </Space>

          {destinationType === BannerDestinationType.Link && (
            <Space direction='vertical' size={8} style={{ width: '100%' }}>
              <Text strong>Link de destino</Text>
              <Input
                value={watch('content.destinationLink') || ''}
                onChange={event =>
                  setValue('content.destinationLink', event.target.value, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                status={destinationLinkError ? 'error' : undefined}
                placeholder='https://www.exemplo.com.br'
                size='large'
              />
              {destinationLinkErrorMessage && (
                <Text type='danger' style={{ fontSize: 12, display: 'block' }}>
                  {destinationLinkErrorMessage}
                </Text>
              )}
            </Space>
          )}

          {destinationType === BannerDestinationType.Product && (
            <Space direction='vertical' size={8} style={{ width: '100%' }}>
              <Text strong>Produto</Text>
              <Select
                styles={{ popup: { root: { background: '#141414' } } }}
                value={watch('content.destinationProduct') || undefined}
                onChange={value =>
                  setValue('content.destinationProduct', value as string, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                options={productOptions}
                loading={isLoadingProducts}
                status={destinationProductError ? 'error' : undefined}
                placeholder='Buscar produto'
                size='large'
                style={{ width: '100%' }}
                showSearch
                filterOption={(input, option) =>
                  String(option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
              {destinationProductErrorMessage && (
                <Text type='danger' style={{ fontSize: 12, display: 'block' }}>
                  {destinationProductErrorMessage}
                </Text>
              )}
            </Space>
          )}

          {destinationType === BannerDestinationType.Category && (
            <Space direction='vertical' size={8} style={{ width: '100%' }}>
              <Text strong>Categoria</Text>
              <Select
                styles={{ popup: { root: { background: '#141414' } } }}
                value={watch('content.destinationCategory') || undefined}
                onChange={value =>
                  setValue('content.destinationCategory', value as string, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                options={categoryOptions}
                loading={isLoadingCategories}
                status={destinationCategoryError ? 'error' : undefined}
                placeholder='Selecione a categoria'
                size='large'
                style={{ width: '100%' }}
              />
              {destinationCategoryErrorMessage && (
                <Text type='danger' style={{ fontSize: 12, display: 'block' }}>
                  {destinationCategoryErrorMessage}
                </Text>
              )}
            </Space>
          )}
        </Space>
      </Card>
    </Section>
  );
};
